import type { Layout } from '../../../data/types';
import type { PolygonIndex } from '../../../geo/polygonIndex';
import type { Vec2 } from '../../../geo/types';
import { obbCorners } from '../../../geo/polygon';
import { createLotPlacer } from '../lotPlacer';
import { obbFromFrontEdge } from '../lotObb';
import { lotFrame } from '../lotFrame';
import { measureLine } from '../measuredLine';
import type { Lot, ReservedFootprint } from '../types';
import { LOT } from '../lotRules';
import type { HouseReading, SurveyedRow } from './rowTypes';
import { readFronts } from './rowFronts';

function point(p: Vec2, dir: Vec2, length: number): Vec2 {
  return [p[0] + dir[0] * length, p[1] + dir[1] * length];
}

/** Stop opposite street rows at their common back line, leaving a narrow shared yard. */
function oppositeDepth(a: Vec2, facing: Vec2, row: SurveyedRow, layout: Layout): number {
  const other = layout.streets.find((s) => s.id === row.opposite);
  if (!other) return row.depth;
  const dir: Vec2 = [-facing[0], -facing[1]];
  let nearest = Infinity;
  for (let i = 1; i < other.centerline.length; i++) {
    const p = other.centerline[i - 1]!,
      q = other.centerline[i]!;
    const edge: Vec2 = [q[0] - p[0], q[1] - p[1]];
    const delta: Vec2 = [p[0] - a[0], p[1] - a[1]];
    const cross = dir[0] * edge[1] - dir[1] * edge[0];
    if (Math.abs(cross) < 1e-8) continue;
    const t = (delta[0] * edge[1] - delta[1] * edge[0]) / cross;
    const u = (delta[0] * dir[1] - delta[1] * dir[0]) / cross;
    if (t > 0 && u >= 0 && u <= 1) nearest = Math.min(nearest, t);
  }
  return Math.min(row.depth, (nearest - other.width / 2 - 2) / 2);
}

function reconstruction(row: SurveyedRow, h: HouseReading): NonNullable<Lot['reconstruction']> {
  return {
    id: h.id,
    row: row.id,
    evidence: row.evidence,
    ...(h.paintedPixel ? { paintedPixel: h.paintedPixel } : {}),
    // Levels are massing estimates, not elevations measured from the painting.
    params: {
      storeys: h.storeys,
      storeyHeight: 3.05,
      roofMaterial: h.roof,
      ...(h.gable ? { frontGable: h.gable } : {}),
    },
  };
}

/** Fitting never moves a traced front sideways or inserts extra houses into a gap. */
export function surveyedRowLots(
  rows: readonly SurveyedRow[],
  layout: Layout,
  buildable: PolygonIndex,
  reserved: readonly ReservedFootprint[],
  ordinalBase = 10_000
): readonly Lot[] {
  const placer = createLotPlacer((p) => buildable.contains(p), [], reserved);
  const placed: Lot[] = [];
  const fits = (lot: Lot) => buildable.obbInside(lot.obb) && placer.fits(lot.obb);
  rows.forEach((row, rowIndex) => {
    const source = layout.streets.find((s) => s.id === row.source);
    if (!source) return;
    const line = measureLine(
      source.centerline,
      source.centerline.map(() => source.width)
    );
    if (Math.max(row.from, row.to) > line.length) return;
    const fronts = readFronts(row, line, source.width, buildable, placer);
    fronts.forEach(({ from: along, to: end, house: h }) => {
      const i = row.houses.indexOf(h);
      const mid = line.pointAt((along + end) / 2);
      const inward: Vec2 =
        row.side === 'left' ? [-mid.tangent[1], mid.tangent[0]] : [mid.tangent[1], -mid.tangent[0]];
      const facing: Vec2 = [-inward[0], -inward[1]];
      const offset = source.width / 2 + LOT.sourceOffset;
      const a = point(line.pointAt(along).point, inward, offset);
      const b = point(line.pointAt(end).point, inward, offset);
      // Tiny drips between neighbouring plots, rather than a new lane between every house.
      const centre = point(mid.point, inward, offset);
      const span: Vec2 = [b[0] - a[0], b[1] - a[1]];
      const width = Math.hypot(...span),
        dir = mid.tangent;
      let footprintWidth = width - 0.08;
      const make = (
        depth: number,
        front = centre,
        toward = facing,
        kind: Lot['kind'] = 'house'
      ): Lot => {
        const obb = obbFromFrontEdge(
          point(front, dir, -footprintWidth / 2),
          point(front, dir, footprintWidth / 2),
          depth,
          toward
        );
        return {
          index: placed.length,
          sourceId: source.id,
          side: row.side,
          ordinal: ordinalBase + rowIndex * 100 + i,
          kind,
          obb,
          corners: obbCorners(obb),
          frontage: footprintWidth,
          depth,
          facing: toward,
          reconstruction: reconstruction(row, h),
        };
      };
      // At a street bend the rectangular reconstruction can overhang a crossing by a few
      // decimetres. Keep its front centre and plot axis, but leave that part as passage.
      while (footprintWidth > Math.max(3.5, width * 0.8) && !fits(make(6))) footprintWidth -= 0.2;
      const limit = row.bank ? 60 : oppositeDepth(centre, facing, row, layout);
      let depth = Math.floor(limit * 4) / 4;
      while (depth >= 6 && !fits(make(depth))) depth -= 0.25;
      if (depth < 6) return;
      if (!row.bank || depth <= 23) {
        const lot = make(depth);
        placed.push(
          row.bank
            ? {
                ...lot,
                reconstruction: { ...lot.reconstruction!, waterfront: 'damrak' },
              }
            : lot
        );
        placer.add(lot);
        return;
      }
      // One house and one warehouse on the same axis. No third, rotated middle filler.
      const frontDepth = Math.min(row.depth, depth - 10);
      const house = make(frontDepth);
      placed.push(house);
      placer.add(house);
      const frame = lotFrame(make(depth));
      // The warehouse addresses the shared yard; its rear wall meets the water.
      const rearDepth = Math.min(16, depth - frontDepth - 2.5);
      const waterFront = point(frame.frontMid, frame.facing, -(depth - rearDepth));
      const warehouse = make(rearDepth, waterFront, frame.facing, 'backhouse');
      const rear: Lot = {
        ...warehouse,
        sourceId: `${source.id}/water`,
        reconstruction: {
          ...warehouse.reconstruction!,
          id: `${h.id}/water`,
          evidence: 'inferred',
          waterfront: 'damrak',
        },
      };
      if (fits(rear)) {
        placed.push(rear);
        placer.add(rear);
      }
    });
  });
  return placed;
}
